import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { useNavigate, Link } from 'react-router-dom';
import EmailConfirmationModal from '../components/EmailConfirmationModal';

const FounderTodolist = ({ userInfo }) => {
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [adminTasks, setAdminTasks] = useState([]);
  const [founderTasks, setFounderTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankInfo, setBankInfo] = useState({
    bankType: 'Mercury',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
    bankAddress: '',
    bankPhone: ''
  });
  const [bankFormLoading, setBankFormLoading] = useState(false);
  const [submittedBankInfo, setSubmittedBankInfo] = useState(null);
  const [showCertificateForm, setShowCertificateForm] = useState(false);
  const [certificateFile, setCertificateFile] = useState(null);
  const [dateOfFormation, setDateOfFormation] = useState('');
  const [certificateUploading, setCertificateUploading] = useState(false);
  const [adminTasksStatus, setAdminTasksStatus] = useState({});
  const [showCertificateConfirmModal, setShowCertificateConfirmModal] = useState(false);

  // Board meeting minutes state
  const [showBoardMinutesForm, setShowBoardMinutesForm] = useState(false);
  const [boardMinutesFile, setBoardMinutesFile] = useState(null);
  const [boardMinutesDate, setBoardMinutesDate] = useState('');
  const [boardMinutesUploading, setBoardMinutesUploading] = useState(false);
  const [showBoardMinutesConfirmModal, setShowBoardMinutesConfirmModal] = useState(false);

  // SBA registration state
  const [showSBAForm, setShowSBAForm] = useState(false);
  const [sbaFile, setSbaFile] = useState(null);
  const [sbaControlId, setSbaControlId] = useState('');
  const [sbaUploading, setSbaUploading] = useState(false);
  const [showSBAConfirmModal, setShowSBAConfirmModal] = useState(false);

  // Assigned admins state
  const [assignedAdmins, setAssignedAdmins] = useState([]);
  // Skip onboarding state
  const [skipOnboarding, setSkipOnboarding] = useState(false);

  // Monthly tasks state for client section
  const [monthlyTasksExpanded, setMonthlyTasksExpanded] = useState(true);
  const [monthlyTransactionStatus, setMonthlyTransactionStatus] = useState({});
  const [displayMonths, setDisplayMonths] = useState([]);
  const [completedTasksExpanded, setCompletedTasksExpanded] = useState(false);
  const [completedMonthlyTasksExpanded, setCompletedMonthlyTasksExpanded] = useState(false);
  const [monthlyCycleSettings, setMonthlyCycleSettings] = useState({
    cycleStartMonth: null,
    isPaused: false
  });
  const [customTaskSettings, setCustomTaskSettings] = useState([]);

  // Generate list of months from cycle start until now (returns empty if no cycle configured)
  const getMonthlyTasks = (cycleStartMonth) => {
    // If no cycle start month is set, return empty array (no tasks)
    if (!cycleStartMonth) {
      return [];
    }

    const months = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    // Use cycle start month
    const [yearStr, monthStr] = cycleStartMonth.split('-');
    const startYear = parseInt(yearStr);
    const startMonthIndex = parseInt(monthStr) - 1; // Convert to 0-indexed

    // Generate months from start until current month
    let year = startYear;
    let monthIndex = startMonthIndex;

    while (year < currentYear || (year === currentYear && monthIndex <= currentMonth)) {
      const monthName = new Date(year, monthIndex, 1).toLocaleString('default', { month: 'long' });
      months.push({
        label: `${monthName} ${year}`,
        value: `${year}-${(monthIndex + 1).toString().padStart(2, '0')}`
      });

      monthIndex++;
      if (monthIndex > 11) {
        monthIndex = 0;
        year++;
      }
    }

    // Reverse to show most recent first
    return months.reverse();
  };

  const monthlyTasks = getMonthlyTasks(monthlyCycleSettings.cycleStartMonth);

  // Define founder to-do tasks
  const defaultFounderTasks = [
    {
      id: 'upload-certificate-of-formation',
      title: 'Upload Certificate of Formation',
      description: 'Upload your LLC Certificate of Formation document and formation date',
      status: 'pending',
      priority: 'high',
      dependsOn: 'form-llc'
    },
    {
      id: 'setup-mercury-bank',
      title: 'Set up Mercury Bank Account',
      description: 'Create a Mercury Bank account using your company email address',
      status: 'pending',
      priority: 'high',
      dependsOn: 'gmail-setup'
    },
    {
      id: 'sign-board-meeting-minutes',
      title: 'Sign Board Meeting Minutes',
      description: 'Download, sign, and upload the board meeting minutes document',
      status: 'pending',
      priority: 'medium',
      dependsOn: 'sam-registration-part-1'
    },
    {
      id: 'assign-so-and-fsr',
      title: 'Assign SO and FSR in eRA Commons',
      description: 'Follow the instructions to assign Signing Official and Financial Services Representative',
      status: 'pending',
      priority: 'medium',
      dependsOn: 'era-registration',
      instructionsUrl: '',
      adminConfirmTask: true // This task is confirmed by admin, not marked complete by founder
    },
    {
      id: 'sba-registration',
      title: 'Complete SBA Registration',
      description: 'Register with the SBA and upload your SBC registration document',
      status: 'pending',
      priority: 'medium',
      dependsOn: 'confirm-so-and-fsr-assignment-in-era-commons'
    },
    {
      id: 'add-streamlaunch-as-aor',
      title: 'Add StreamLaunch as AOR in Grants.gov',
      description: 'Follow the instructions to add StreamLaunch as an Authorized Organization Representative',
      status: 'pending',
      priority: 'medium',
      dependsOn: 'grants.gov-registration',
      instructionsUrl: '',
      adminConfirmTask: true // This task is confirmed by admin, not marked complete by founder
    }
  ];

  useEffect(() => {
    if (userInfo) {
      fetchFounderData();
      fetchAssignedAdmins();
      fetchMonthlyCycleSettings();
      fetchCustomTaskSettings();
    }
  }, [userInfo]);

  const fetchCustomTaskSettings = async () => {
    try {
      // Only fetch client-visible task settings (for due date info)
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/client-settings`
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTaskSettings(data.tasks || []);
      }
    } catch (error) {
      console.error('Error fetching client task settings:', error);
    }
  };

  // Find the confirm transactions task's due date info
  const getConfirmTransactionsDueDate = () => {
    const confirmTask = customTaskSettings.find(t =>
      t.title && t.title.toLowerCase().includes('confirm') &&
      t.title.toLowerCase().includes('transaction')
    );
    if (confirmTask) {
      // For recurring tasks, dueDate stores a day-of-month value (e.g. "20")
      if (confirmTask.dueDate && confirmTask.isRepeated === 'true') {
        const day = parseInt(confirmTask.dueDate, 10);
        if (!isNaN(day)) {
          const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                         day === 2 || day === 22 ? 'nd' :
                         day === 3 || day === 23 ? 'rd' : 'th';
          return `Due by the ${day}${suffix} of each month`;
        }
      }
      // For one-time tasks, dueDate is a YYYY-MM-DD date
      if (confirmTask.dueDate && confirmTask.isRepeated !== 'true') {
        return `Due: ${new Date(confirmTask.dueDate + 'T00:00:00').toLocaleDateString()}`;
      }
    }
    return null;
  };

  // Fetch monthly transaction status after cycle settings are loaded
  useEffect(() => {
    if (userInfo) {
      fetchMonthlyTransactionStatus();
    }
  }, [userInfo, monthlyCycleSettings.cycleStartMonth]);

  const fetchMonthlyCycleSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthlyCycleSettings(data);
      }
    } catch (error) {
      console.error('Error fetching monthly cycle settings:', error);
    }
  };

  const fetchMonthlyTransactionStatus = async () => {
    try {
      const statusMap = {};
      const allMonthsToShow = new Set(monthlyTasks.map(m => m.value));

      // First, get all months with unconfirmed transactions (including beyond 6 months)
      try {
        const unconfirmedMonthsResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_API_URL}/expense/months-with-unconfirmed`,
          {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );

        if (unconfirmedMonthsResponse.ok) {
          const data = await unconfirmedMonthsResponse.json();
          // Add any months with unconfirmed transactions to our display list
          (data.months || []).forEach(month => {
            allMonthsToShow.add(month.value);
          });
        }
      } catch (error) {
        console.error('Error fetching months with unconfirmed:', error);
      }

      // Now fetch status for each month we want to display
      const monthsArray = Array.from(allMonthsToShow).map(value => {
        const [year, month] = value.split('-');
        const monthName = new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleString('default', { month: 'long' });
        return {
          value,
          label: `${monthName} ${year}`
        };
      });

      await Promise.all(monthsArray.map(async (month) => {
        const [year, monthNum] = month.value.split('-');

        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_API_URL}/expense/month-status?year=${year}&month=${monthNum}`,
            {
              method: 'GET',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' }
            }
          );

          if (response.ok) {
            const data = await response.json();
            statusMap[month.value] = {
              label: month.label,
              totalCount: data.totalCount || 0,
              unconfirmedCount: data.unconfirmedCount || 0,
              hasTransactions: data.hasTransactions || false,
              hasUnconfirmed: data.hasUnconfirmed || false,
              allConfirmed: data.allConfirmed || false
            };
          } else {
            statusMap[month.value] = {
              label: month.label,
              totalCount: 0,
              unconfirmedCount: 0,
              hasTransactions: false,
              hasUnconfirmed: false,
              allConfirmed: false
            };
          }
        } catch (error) {
          console.error(`Error fetching status for ${month.label}:`, error);
          statusMap[month.value] = {
            label: month.label,
            totalCount: 0,
            unconfirmedCount: 0,
            hasTransactions: false,
            hasUnconfirmed: false,
            allConfirmed: false
          };
        }
      }));

      setMonthlyTransactionStatus(statusMap);
      
      // Update display months - sort by date (most recent first)
      const sortedMonths = monthsArray.sort((a, b) => {
        return b.value.localeCompare(a.value);
      });

      console.log("sortedMonths: ", sortedMonths);
      setDisplayMonths(sortedMonths);
    } catch (error) {
      console.error('Error fetching monthly transaction status:', error);
    }
  };

  const fetchAssignedAdmins = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/auth/company/assigned-admins`
      );

      if (response.ok) {
        const data = await response.json();
        setAssignedAdmins(data.assignedAdmins || []);
      }
    } catch (error) {
      console.error('Error fetching assigned admins:', error);
      // Fallback to default admin email if fetch fails
      setAssignedAdmins([{ email: 'admin@streamlaunch.com', name: 'Admin Team', role: 'admin' }]);
    }
  };

  const fetchFounderData = async () => {
    setLoading(true);
    try {
      // Check if company has skipOnboarding enabled (fetch first so we can
      // synthesize a submission if needed)
      let skipOnboardingFromCompany = false;
      let companyNameFromCompany = userInfo?.companyName || '';
      if (userInfo?.companyName) {
        try {
          const companiesResponse = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/auth/companies`
          );
          if (companiesResponse.ok) {
            const companies = await companiesResponse.json();
            const userCompany = companies.find(c => c.name === userInfo.companyName);
            if (userCompany) {
              companyNameFromCompany = userCompany.name || companyNameFromCompany;
              if (userCompany.skipOnboarding === 'true' || userCompany.skipOnboarding === true) {
                skipOnboardingFromCompany = true;
                setSkipOnboarding(true);
              }
            }
          }
        } catch (companyError) {
          console.error('Error fetching company skipOnboarding status:', companyError);
        }
      }

      // Get company formation submissions for this founder
      const submissionsResponse = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submissions/founder`
      );

      let latestSubmission = null;
      if (submissionsResponse.ok) {
        const submissions = await submissionsResponse.json();
        latestSubmission = submissions[0]; // Get most recent submission
        setSubmission(latestSubmission);

        if (latestSubmission) {
          // Get admin tasks for this submission
          console.log("latest submission: ", latestSubmission);
          const tasksResponse = await api.get(
            `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/submission/${latestSubmission.ID}/tasks`
          );

          if (tasksResponse.ok) {
            const adminTasksData = await tasksResponse.json();
            setAdminTasks(adminTasksData.tasks);

            // After setting admin tasks, get founder tasks and update their instructions URLs
            const founderTasksResponse = await api.get(
              `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/tasks`
            );

            let tasksToSet = defaultFounderTasks;
            if (founderTasksResponse.ok) {
              const founderTasksData = await founderTasksResponse.json();
              // The endpoint returns an object { adminProgress, founderTasks,
              // savedTasks, progressStats }. Merge the persisted statuses into
              // the full default task list (by id) so completed tasks stay
              // completed after a refresh instead of resetting to pending.
              const savedById = {};
              const collect = (arr) => (Array.isArray(arr) ? arr : []).forEach(t => {
                if (t && t.id) savedById[t.id] = { ...savedById[t.id], ...t };
              });
              collect(founderTasksData.founderTasks);
              collect(founderTasksData.savedTasks);
              // Support both the current object response and a legacy array response.
              if (Array.isArray(founderTasksData)) collect(founderTasksData);

              tasksToSet = defaultFounderTasks.map(t => {
                const saved = savedById[t.id];
                if (!saved || !saved.status) return t;
                return { ...t, status: saved.status, completedAt: saved.completedAt || t.completedAt };
              });
            }

            // Update instructions URLs from admin tasks if available
            if (adminTasksData && adminTasksData.tasks) {
              tasksToSet = tasksToSet.map(task => {
                if (task.id === 'assign-so-and-fsr') {
                  const eraTask = adminTasksData.tasks.find(t => t.taskName === 'eRA Registration');
                  console.log("eraTask for SO/FSR: ", eraTask);
                  if (eraTask && eraTask.instructionsUrl) {
                    return { ...task, instructionsUrl: eraTask.instructionsUrl };
                  }
                } else if (task.id === 'add-streamlaunch-as-aor') {
                  const grantsGovTask = adminTasksData.tasks.find(t => t.taskName === 'Grants.gov Registration');
                  console.log("grantsGovTask for AOR: ", grantsGovTask);
                  if (grantsGovTask && grantsGovTask.instructionsUrl) {
                    return { ...task, instructionsUrl: grantsGovTask.instructionsUrl };
                  }
                }
                return task;
              });
            }

            setFounderTasks(tasksToSet);
            console.log("founderTasks with updated instructions: ", tasksToSet);
          }
        }
      } else {
        // If no submission, just use default founder tasks
        setFounderTasks(defaultFounderTasks);
      }

      // If the company has skipOnboarding and there is no real submission,
      // synthesize one so the normal todolist view renders (instead of the
      // "Welcome to Your Dashboard" / formation-form prompts).
      if (skipOnboardingFromCompany && !latestSubmission) {
        const syntheticSubmission = {
          ID: 'no-submission',
          'Company Name': companyNameFromCompany,
          'Submitted At': new Date().toISOString(),
          'Founders JSON': '[]',
          founders: '[]',
          companyAlreadyFormed: false
        };
        setSubmission(syntheticSubmission);
        setFounderTasks(defaultFounderTasks);
        // Don't try to fetch admin tasks for a non-existent submission
      }

      // Get notifications
      fetchNotifications();

      // Get submitted bank info
      fetchSubmittedBankInfo();

    } catch (error) {
      console.error('Error fetching founder data:', error);
      setError('Failed to load dashboard data');
    }
    setLoading(false);
  };

  const fetchNotifications = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/notifications`
      );

      if (response.ok) {
        const notificationsData = await response.json();
        setNotifications(notificationsData);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchSubmittedBankInfo = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/admin/bank-info`
      );

      if (response.ok) {
        const bankData = await response.json();
        console.log("bankData:", bankData);
        // Find bank info submitted by this user
        const userBankInfo = bankData.bankInfo?.find(info => info.submittedBy === userInfo?.username);
        setSubmittedBankInfo(userBankInfo || null);
      }
    } catch (error) {
      console.error('Error fetching submitted bank info:', error);
    }
  };

  const updateFounderTask = async (taskId, status) => {
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/tasks/${taskId}`,
        { status }
      );
      
      if (response.ok) {
        setFounderTasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, status, completedAt: status === 'completed' ? new Date().toISOString() : null } : task
        ));
      }
    } catch (error) {
      console.error('Error updating founder task:', error);
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await api.delete(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/notifications/${notificationId}`
      );
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  // Overall formation progress percentage (counts both founder + admin tasks)
  const getProgressPercentage = () => {
    const completedFounderTasks = founderTasks.filter(task => task.status === 'completed').length;
    const completedAdminTasks = adminTasks.filter(task => task.status === 'completed').length;
    const totalFounderTasks = founderTasks.length;
    const totalAdminTasks = adminTasks.length;
    const totalCompleted = completedFounderTasks + completedAdminTasks;
    const totalTasks = totalFounderTasks + totalAdminTasks;
    return totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0;
  };

  // Calculate formation tasks completion stats for founder tasks
  const getFormationTasksStats = () => {
    // Count completed founder tasks
    const completedFounderTasks = founderTasks.filter(task => task.status === 'completed').length;
    // Count completed admin tasks (these are formation admin tasks)
    const completedAdminTasks = adminTasks.filter(task => task.status === 'completed').length;
    // Total tasks
    const totalFounderTasks = founderTasks.length;
    const totalAdminTasks = adminTasks.length;
    
    const totalCompleted = completedFounderTasks + completedAdminTasks;
    const totalTasks = totalFounderTasks + totalAdminTasks;
    
    return {
      completedFounderTasks,
      completedAdminTasks,
      totalFounderTasks,
      totalAdminTasks,
      totalCompleted,
      totalTasks,
      percentage: totalTasks > 0 ? Math.round((totalCompleted / totalTasks) * 100) : 0,
      isComplete: totalCompleted === totalTasks && totalTasks > 0
    };
  };

  // const getProgressStatus = () => {
  //   console.log("adminTasks: ", adminTasks);
  //   const completedTasks = adminTasks.filter(task => task.status === 'completed').length;
    
  //   if (completedTasks === 0) return 'Getting Started';
  //   if (completedTasks < 3) return 'Initial Setup';
  //   if (completedTasks < 6) return 'Legal Formation';
  //   if (completedTasks < 9) return 'Registrations';
  //   if (completedTasks < adminTasks.length) return 'Final Steps';
  //   return 'Ready for SBIR Submission';
  // };

  const getTaskStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <span className="text-green-600 text-xl">✓</span>;
      case 'in_progress':
        return <span className="text-yellow-600 text-xl">⏳</span>;
      default:
        return <span className="text-gray-400 text-xl">○</span>;
    }
  };

  const getTaskColor = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-200 bg-green-50';
      case 'in_progress':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const isTaskAvailable = (task) => {
    if (!task.dependsOn) return true;

    // Bank setup requires BOTH Gmail AND EIN completion
    if (task.id === 'setup-mercury-bank') {
      const gmailTask = adminTasks.find(t => t.taskName.toLowerCase().includes('gmail'));
      const einTask = adminTasks.find(t => t.taskName === 'Apply for EIN');

      const gmailCompleted = gmailTask && gmailTask.status === 'completed';
      const einCompleted = einTask && einTask.status === 'completed';

      return gmailCompleted && einCompleted;
    }

    // SO/FSR assignment - available after eRA registration admin task completes
    if (task.id === 'assign-so-and-fsr') {
      const eraTask = adminTasks.find(t => t.taskName === 'eRA Registration');
      const eraCompleted = eraTask && eraTask.status === 'completed';
      return eraCompleted;
    }

    // AOR assignment requires Grants.gov admin task and SO/FSR confirmation to be completed
    if (task.id === 'add-streamlaunch-as-aor') {
      // First check if Grants.gov admin task is completed
      const grantsGovTask = adminTasks.find(t => t.taskName === 'Grants.gov Registration');
      console.log("grantsGovTask: ", grantsGovTask);
      const grantsGovCompleted = grantsGovTask && grantsGovTask.status === 'completed';

      // Also check if admin has confirmed SO/FSR assignment in eRA Commons
      const soFsrTask = adminTasks.find(t => t.taskName === 'Confirm SO and FSR Assignment in eRA Commons');
      const soFsrCompleted = soFsrTask && soFsrTask.status === 'completed';

      console.log("grantsGovCompleted: ", grantsGovCompleted);
      console.log("soFsrCompleted: ", soFsrCompleted);

      return grantsGovCompleted && soFsrCompleted;
    }

    // Check if Gmail setup is completed for other tasks
    if (task.dependsOn === 'gmail-setup') {
      const gmailTask = adminTasks.find(t => t.taskName.toLowerCase().includes('gmail'));
      return gmailTask && gmailTask.status === 'completed';
    }

    // Convert kebab-case to space-separated and match with admin task names
    const dependsOnName = task.dependsOn.replace(/-/g, ' ').toLowerCase();
    const dependentTask = adminTasks.find(t => t.taskName.toLowerCase().includes(dependsOnName));
    return dependentTask && dependentTask.status === 'completed';
  };

  const fetchAdminTasksStatus = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/admin-tasks-status`
      );

      if (response.ok) {
        const data = await response.json();
        setAdminTasksStatus(data.taskStatuses || {});
      }
    } catch (error) {
      console.error('Error fetching admin tasks status:', error);
    }
  };

  // Poll admin tasks status every 30 seconds
  useEffect(() => {
    if (userInfo) {
      fetchAdminTasksStatus();
      const interval = setInterval(fetchAdminTasksStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [userInfo]);

  const handleBankInfoSubmit = async (e) => {
    e.preventDefault();
    setBankFormLoading(true);

    try {
      // Submit bank info to backend
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/bank-info`,
        bankInfo
      );

      if (response.ok) {
        // Mark Mercury bank task as completed
        await updateFounderTask('setup-mercury-bank', 'completed');
        setShowBankForm(false);
        setBankInfo({
          bankName: '',
          routingNumber: '',
          accountNumber: '',
          bankAddress: '',
          bankPhone: ''
        });

        // Refresh data
        fetchFounderData();
        fetchSubmittedBankInfo();
      } else {
        setError('Failed to submit bank information');
      }
    } catch (error) {
      console.error('Error submitting bank info:', error);
      setError('Failed to submit bank information');
    }

    setBankFormLoading(false);
  };

  const handleBankInfoChange = (field, value) => {
    setBankInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCertificateUpload = async (e) => {
    e.preventDefault();

    if (!certificateFile || !dateOfFormation) {
      setError('Please provide both the certificate file and date of formation');
      return;
    }

    // Show confirmation modal before uploading
    setShowCertificateConfirmModal(true);
  };

  const confirmCertificateUpload = async () => {
    setCertificateUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', certificateFile);
      formData.append('dateOfFormation', dateOfFormation);

      // Don't set Content-Type header manually - let the browser set it with the boundary
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/upload-certificate`,
        formData
      );

      if (response.ok) {
        setShowCertificateConfirmModal(false);
        setShowCertificateForm(false);
        setCertificateFile(null);
        setDateOfFormation('');

        // Show success message
        setSuccessMessage('Certificate of Formation uploaded successfully! Admin has been notified.');
        setTimeout(() => setSuccessMessage(''), 5000);

        // Refresh data to show updated task status
        fetchFounderData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to upload certificate');
        setTimeout(() => setError(''), 5000);
        setShowCertificateConfirmModal(false);
      }
    } catch (error) {
      console.error('Error uploading certificate:', error);
      setError('Failed to upload certificate');
      setTimeout(() => setError(''), 5000);
      setShowCertificateConfirmModal(false);
    }

    setCertificateUploading(false);
  };

  const handleBoardMinutesUpload = async (e) => {
    e.preventDefault();

    if (!boardMinutesFile) {
      setError('Please provide the signed board meeting minutes document');
      return;
    }

    if (!boardMinutesDate) {
      setError('Please provide the meeting date');
      return;
    }

    // Show confirmation modal before uploading
    setShowBoardMinutesConfirmModal(true);
  };

  const confirmBoardMinutesUpload = async () => {
    setBoardMinutesUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', boardMinutesFile);
      formData.append('meetingDate', boardMinutesDate);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/upload-board-minutes`,
        formData
      );

      if (response.ok) {
        setShowBoardMinutesConfirmModal(false);
        setShowBoardMinutesForm(false);
        setBoardMinutesFile(null);
        setBoardMinutesDate('');

        // Show success message
        setSuccessMessage('Board Meeting Minutes uploaded successfully! Admin has been notified.');
        setTimeout(() => setSuccessMessage(''), 5000);

        // Refresh data to show updated task status
        fetchFounderData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to upload board meeting minutes');
        setTimeout(() => setError(''), 5000);
        setShowBoardMinutesConfirmModal(false);
      }
    } catch (error) {
      console.error('Error uploading board meeting minutes:', error);
      setError('Failed to upload board meeting minutes');
      setTimeout(() => setError(''), 5000);
      setShowBoardMinutesConfirmModal(false);
    }

    setBoardMinutesUploading(false);
  };

  const handleSBAUpload = async (e) => {
    e.preventDefault();

    if (!sbaFile || !sbaControlId) {
      setError('Please provide both the SBC registration document and control ID');
      return;
    }

    // Show confirmation modal before uploading
    setShowSBAConfirmModal(true);
  };

  const confirmSBAUpload = async () => {
    setSbaUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', sbaFile);
      formData.append('sbaControlId', sbaControlId);

      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/company-formation/founder/upload-sba-registration`,
        formData
      );

      if (response.ok) {
        setShowSBAConfirmModal(false);
        setShowSBAForm(false);
        setSbaFile(null);
        setSbaControlId('');

        // Show success message
        setSuccessMessage('SBA Registration submitted successfully! Admin has been notified.');
        setTimeout(() => setSuccessMessage(''), 5000);

        // Refresh data to show updated task status
        fetchFounderData();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to submit SBA registration');
        setTimeout(() => setError(''), 5000);
        setShowSBAConfirmModal(false);
      }
    } catch (error) {
      console.error('Error submitting SBA registration:', error);
      setError('Failed to submit SBA registration');
      setTimeout(() => setError(''), 5000);
      setShowSBAConfirmModal(false);
    }

    setSbaUploading(false);
  };

  if (!userInfo || !['founder', 'super_user', 'super_client'].includes(userInfo.accountType)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">This dashboard is only available for founders.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="card p-6 max-w-md mx-auto">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="card p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Your Founder Dashboard</h1>
            <p className="text-gray-600 mb-6">
              Start your company formation journey by completing your company formation form.
            </p>
            <button
              onClick={() => navigate('/company-formation')}
              className="btn-primary inline-block"
            >
              Begin Company Formation Form
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="card p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Todolist</h1>
              <p className="text-gray-600 mt-1">
                Todolist for <strong>{submission['Company Name']}</strong>
              </p>
            </div>
            {!skipOnboarding && adminTasks.length > 0 && (
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{getProgressPercentage()}%</div>
                <div className="text-sm text-gray-500">Formation Progress</div>
              </div>
            )}
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="p-4 rounded-lg bg-green-50 border-l-4 border-green-400 text-green-700">
            <p className="font-medium">{successMessage}</p>
          </div>
        )}
        {error && (
          <div className="p-4 rounded-lg bg-red-50 border-l-4 border-red-400 text-red-700">
            <p className="font-medium">{error}</p>
          </div>
        )}

        {/* Notifications */}
        {/* {notifications.length > 0 && (
          <div className="space-y-3">
            {notifications.map((notification, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${
                notification.type === 'success' ? 'bg-green-50 border-green-400 text-green-700' :
                notification.type === 'warning' ? 'bg-yellow-50 border-yellow-400 text-yellow-700' :
                'bg-blue-50 border-blue-400 text-blue-700'
              }`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{notification.title}</p>
                    <p className="text-sm mt-1">{notification.message}</p>
                  </div>
                  <button
                    onClick={() => dismissNotification(notification.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )} */}

        {/* Monthly Tasks Section - Client Section from TodoList */}
        <div className="card p-6 mb-6">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setMonthlyTasksExpanded(!monthlyTasksExpanded)}
          >
            <h2 className="text-xl font-semibold text-gray-900">Tasks</h2>
            <span className="text-gray-500">
              {monthlyTasksExpanded ? '▼' : '▶'}
            </span>
          </div>
          <p className="text-gray-600 text-sm mt-1 mb-4">Review and confirm monthly financial transactions</p>

          {monthlyTasksExpanded && (
            <div className="space-y-3">
              {!monthlyCycleSettings.cycleStartMonth ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-500">Monthly tasks cycle has not been started yet.</p>
                  <p className="text-gray-400 text-sm mt-1">Your admin will configure the monthly tasks schedule.</p>
                </div>
              ) : Object.keys(monthlyTransactionStatus).length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-gray-500">Loading monthly tasks...</p>
                </div>
              ) : (
                <>
                  {/* Pending Tasks - months with unconfirmed transactions */}
                  {displayMonths.filter(month =>
                    monthlyTransactionStatus[month.value]?.hasUnconfirmed
                  ).length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Pending Review</h3>
                      <div className="space-y-2">
                        {displayMonths
                          .filter(month => monthlyTransactionStatus[month.value]?.hasUnconfirmed)
                          .map((month) => {
                            const status = monthlyTransactionStatus[month.value];
                            return (
                              <Link
                                key={month.value}
                                to={`/grant-fund-financials?tab=unconfirmed&month=${month.value}`}
                                className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50 transition-colors duration-200 hover:bg-amber-100"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </span>
                                  <div>
                                    <p className="font-medium text-gray-900">Review Transactions: {status?.label || month.label}</p>
                                    <p className="text-sm text-amber-700">
                                      {status?.unconfirmedCount || 0} transaction{(status?.unconfirmedCount || 0) !== 1 ? 's' : ''} need review
                                      {getConfirmTransactionsDueDate() && (
                                        <span className="ml-2 text-amber-600 font-medium">({getConfirmTransactionsDueDate()})</span>
                                      )}
                                    </p>
                                  </div>
                                </div>
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Waiting for Admin Import - months with no transactions */}
                  {displayMonths.filter(month =>
                    monthlyTransactionStatus[month.value] && !monthlyTransactionStatus[month.value]?.hasTransactions
                  ).length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Waiting for Admin Import</h3>
                      <div className="space-y-2">
                        {displayMonths
                          .filter(month => monthlyTransactionStatus[month.value] && !monthlyTransactionStatus[month.value]?.hasTransactions)
                          .map((month) => {
                            const status = monthlyTransactionStatus[month.value];
                            return (
                              <div
                                key={month.value}
                                className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </span>
                                  <div>
                                    <p className="font-medium text-gray-900">Transactions: {status?.label || month.label}</p>
                                    <p className="text-sm text-gray-500">Waiting for admin to import transactions</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Completed monthly tasks moved to unified Completed Tasks section below */}

                  {/* No Tasks Message - only show if all months have been checked and none have data */}
                  {displayMonths.length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                      </div>
                      <p className="text-gray-600 font-medium">No transactions to review</p>
                      <p className="text-sm text-gray-500 mt-1">Financial data will appear here once available</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Founder Tasks - hidden for skip-onboarding companies, or when all formation tasks are complete */}
        {!skipOnboarding && !getFormationTasksStats().isComplete && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Formation Tasks</h2>
              {/* Formation Progress Indicator (badge only - progress bar removed) */}
              {(() => {
                const stats = getFormationTasksStats();
                return stats.isComplete ? (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <span>Complete</span>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Company Formation Form Button */}
            <button
              onClick={() => navigate('/company-formation')}
              className="w-full p-4 mb-4 bg-amber-600 text-white rounded-lg hover:bg-amber-700 hover:shadow-lg transition-all duration-200 text-left group border-2 border-amber-700 relative"
            >
              {/* Completion Badge - Shows when founder has submitted their personal info */}
              {(() => {
                if (!submission) return null;

                // Check if current founder has completed their personal info
                const currentUserEmail = userInfo?.email || userInfo?.username;
                let founders = [];
                try {
                  const foundersData = submission['Founders JSON'] || submission.founders || '[]';
                  founders = typeof foundersData === 'string' ? JSON.parse(foundersData) : foundersData;
                } catch (e) {
                  console.error('Error parsing founders:', e);
                }

                const currentFounder = founders.find(f => f.email === currentUserEmail);
                const hasCompletedPersonalInfo = currentFounder &&
                                                 currentFounder.fullName &&
                                                 currentFounder.cellPhone &&
                                                 currentFounder.dateOfBirth &&
                                                 currentFounder.homeAddress;

                return hasCompletedPersonalInfo ? (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full p-1.5 shadow-lg border-2 border-white">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ) : null;
              })()}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center group-hover:bg-amber-400 transition-all duration-200">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-base font-semibold text-white">Company Formation Form</p>
                    <p className="text-sm text-amber-100">
                      {(() => {
                        if (!submission) return 'View and edit your company formation details';

                        const currentUserEmail = userInfo?.email || userInfo?.username;
                        let founders = [];
                        try {
                          const foundersData = submission['Founders JSON'] || submission.founders || '[]';
                          founders = typeof foundersData === 'string' ? JSON.parse(foundersData) : foundersData;
                        } catch (e) {
                          return 'View and edit your company formation details';
                        }

                        const currentFounder = founders.find(f => f.email === currentUserEmail);
                        const hasCompletedPersonalInfo = currentFounder &&
                                                         currentFounder.fullName &&
                                                         currentFounder.cellPhone &&
                                                         currentFounder.dateOfBirth &&
                                                         currentFounder.homeAddress;

                        return hasCompletedPersonalInfo
                          ? 'Personal Info Submitted - Click to view'
                          : 'Complete your personal information';
                      })()}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>

            {(() => {
              const isTaskCompleted = (task) => {
                console.log("task: ", task);
                console.log("task status: ", task.status);
                if (task.status === 'completed') return true;
                if (task.id === 'setup-mercury-bank' && submittedBankInfo) return true;
                return false;
              };

              const renderTaskRow = (task, index) => (
                <div key={index} className={`p-3 rounded-lg border ${getTaskColor(isTaskCompleted(task) ? 'completed' : task.status)} ${
                  !isTaskAvailable(task) || task.locked ? 'opacity-60' : ''
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {isTaskCompleted(task) && (
                          <span className="text-green-600 text-base flex-shrink-0">✓</span>
                        )}
                        <p className="text-sm font-medium text-gray-900">{task.title}</p>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                    </div>
                    <div className="flex-shrink-0 ml-3">
                      {isTaskCompleted(task) ? (
                        <span className="text-green-600 text-lg">✓</span>
                      ) : isTaskAvailable(task) && task.id === 'upload-certificate-of-formation' && !task.locked ? (
                        <button
                          onClick={() => setShowCertificateForm(true)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Upload Certificate
                        </button>
                      ) : isTaskAvailable(task) && task.id === 'setup-mercury-bank' && !submittedBankInfo ? (
                        <button
                          onClick={() => setShowBankForm(true)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Submit Bank Info
                        </button>
                      ) : isTaskAvailable(task) && task.id === 'sign-board-meeting-minutes' && !task.locked ? (
                        <button
                          onClick={() => setShowBoardMinutesForm(true)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Upload Signed Doc
                        </button>
                      ) : isTaskAvailable(task) && task.id === 'sba-registration' && !task.locked ? (
                        <button
                          onClick={() => setShowSBAForm(true)}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Submit Registration
                        </button>
                      ) : isTaskAvailable(task) && (task.id === 'add-streamlaunch-as-aor' || task.id === 'assign-so-and-fsr') && !task.locked ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={task.instructionsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 inline-block"
                          >
                            View Instructions
                          </a>
                          {(task.id === 'add-streamlaunch-as-aor' || task.id === 'assign-so-and-fsr') && task.status !== 'completed' && (
                            <button
                              onClick={() => updateFounderTask(task.id, 'completed')}
                              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                            >
                              Mark Complete
                            </button>
                          )}
                        </div>
                      ) : isTaskAvailable(task) && task.locked ? (
                        <span className="text-gray-400 text-xs">Locked</span>
                      ) : isTaskAvailable(task) ? (
                        <button
                          onClick={() => updateFounderTask(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                          className="text-gray-400 hover:text-green-600 text-lg"
                        >
                          ○
                        </button>
                      ) : (
                        <span className="text-gray-300 text-lg">○</span>
                      )}
                    </div>
                  </div>
                  {!isTaskAvailable(task) && (
                    <p className="text-xs text-gray-500 mt-2">
                      Waiting for: {task.id === 'setup-mercury-bank' ? 'Gmail Setup and EIN' : task.dependsOn?.replace(/-/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase())}
                    </p>
                  )}
                </div>
              );

              const availableTasks = founderTasks.filter(t => !isTaskCompleted(t) && isTaskAvailable(t) && !t.locked);
              const completedTasksList = founderTasks.filter(t => isTaskCompleted(t));
              const lockedTasks = founderTasks.filter(t => !isTaskCompleted(t) && (!isTaskAvailable(t) || t.locked));

              return (
                <div className="space-y-4">
                  {availableTasks.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Ready to Complete</h3>
                        <span className="text-xs text-gray-500">({availableTasks.length})</span>
                      </div>
                      <div className="space-y-3">
                        {availableTasks.map((task, idx) => renderTaskRow(task, `avail-${idx}`))}
                      </div>
                    </div>
                  )}

                  {completedTasksList.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Completed</h3>
                        <span className="text-xs text-gray-500">({completedTasksList.length})</span>
                      </div>
                      <div className="space-y-3">
                        {completedTasksList.map((task, idx) => renderTaskRow(task, `done-${idx}`))}
                      </div>
                    </div>
                  )}

                  {lockedTasks.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Locked / Waiting on Earlier Steps</h3>
                        <span className="text-xs text-gray-500">({lockedTasks.length})</span>
                      </div>
                      <div className="space-y-3">
                        {lockedTasks.map((task, idx) => renderTaskRow(task, `lock-${idx}`))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Mercury Bank Setup Instructions */}
            {/* {isTaskAvailable(founderTasks.find(t => t.id === 'setup-mercury-bank')) && !submittedBankInfo && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">🏦 Next Step: Mercury Bank Setup</h3>
                <p className="text-xs text-blue-700 mb-3">
                  Your company Gmail is ready! Now create a Mercury Bank account using your new company email, then submit the bank details below.
                </p>
                <a
                  href={process.env.REACT_APP_MERCURY_REFERRAL_URL || "https://mercury.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  Open Mercury Bank →
                </a>
              </div>
            )} */}

            {/* Bank Setup Complete */}
            {submittedBankInfo && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-semibold text-green-900 mb-2">✅ Mercury Bank Setup Complete</h3>
                <p className="text-xs text-green-700">
                  Your bank account information has been submitted and is available for admin review.
                </p>
              </div>
            )}
          </div>

          {/* Bank Info Form Column */}
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Progress Overview</h2>
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Formation Progress</span>
                <span className="text-sm text-gray-500">{getProgressPercentage()}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
              {/* <p className="text-xs text-gray-500 mt-2">{getProgressStatus()}</p> */}
            </div>

            {/* Submitted Bank Information Display */}
            {submittedBankInfo && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">✅ Bank Account Information</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Bank Name</p>
                      <p className="text-gray-900">{submittedBankInfo.bankName || 'Mercury Bank'}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700">Routing Number</p>
                      <p className="text-gray-900 font-mono">{submittedBankInfo.routingNumber}</p>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700">Account Number</p>
                      <p className="text-gray-900 font-mono">
                        {submittedBankInfo.accountNumber ? `****${submittedBankInfo.accountNumber.slice(-4)}` : 'Not provided'}
                      </p>
                    </div>

                    {submittedBankInfo.bankAddress && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Bank Address</p>
                        <p className="text-gray-900 text-sm">{submittedBankInfo.bankAddress}</p>
                      </div>
                    )}

                    {submittedBankInfo.bankPhone && (
                      <div>
                        <p className="text-sm font-medium text-gray-700">Bank Phone</p>
                        <p className="text-gray-900">{submittedBankInfo.bankPhone}</p>
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-green-200">
                    <div className="flex items-center text-sm text-green-700">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Submitted on {new Date(submittedBankInfo.submittedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Bank Information Form */}
            {showBankForm && !submittedBankInfo && (
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Bank Account Information</h3>
                <form onSubmit={handleBankInfoSubmit} className="space-y-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Type *
                    </label>
                    <select
                      value={bankInfo.bankType}
                      onChange={(e) => {
                        handleBankInfoChange('bankType', e.target.value);
                        // Clear optional fields when switching bank types
                        if (e.target.value === 'Mercury') {
                          handleBankInfoChange('bankAddress', '');
                          handleBankInfoChange('bankPhone', '');
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="Mercury">Mercury Bank</option>
                      <option value="BoA">Bank of America</option>
                      <option value="PNC">PNC Bank</option>
                    </select>
                    {bankInfo.bankType === 'Mercury' && (
                      <p className="text-xs text-blue-600 mt-1">
                        <a href={process.env.REACT_APP_MERCURY_REFERRAL_URL || 'https://mercury.com'} target="_blank" rel="noopener noreferrer" className="underline">
                          Use our Mercury referral link →
                        </a>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Routing Number *
                    </label>
                    <input
                      type="text"
                      value={bankInfo.routingNumber}
                      onChange={(e) => handleBankInfoChange('routingNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Number *
                    </label>
                    <input
                      type="text"
                      value={bankInfo.accountNumber}
                      onChange={(e) => handleBankInfoChange('accountNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  {(bankInfo.bankType === 'BoA' || bankInfo.bankType === 'PNC') && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bank Address *
                        </label>
                        <textarea
                          value={bankInfo.bankAddress}
                          onChange={(e) => handleBankInfoChange('bankAddress', e.target.value)}
                          placeholder="Street, City, State, ZIP"
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bank Phone *
                        </label>
                        <input
                          type="tel"
                          value={bankInfo.bankPhone}
                          onChange={(e) => handleBankInfoChange('bankPhone', e.target.value)}
                          placeholder="(XXX) XXX-XXXX"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                    </>
                  )}

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={bankFormLoading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {bankFormLoading ? 'Submitting...' : 'Submit Bank Info'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBankForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Certificate Upload Form */}
            {showCertificateForm && (
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Certificate of Formation</h3>
                <form onSubmit={handleCertificateUpload} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Certificate of Formation Document *
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setCertificateFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, PNG</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Formation *
                    </label>
                    <input
                      type="date"
                      value={dateOfFormation}
                      onChange={(e) => setDateOfFormation(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={certificateUploading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {certificateUploading ? 'Uploading...' : 'Upload Certificate'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCertificateForm(false);
                        setCertificateFile(null);
                        setDateOfFormation('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Board Meeting Minutes Upload Form */}
            {showBoardMinutesForm && (
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Signed Board Meeting Minutes</h3>
                <form onSubmit={handleBoardMinutesUpload} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meeting Date *
                    </label>
                    <input
                      type="date"
                      value={boardMinutesDate}
                      onChange={(e) => setBoardMinutesDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the date shown on the board meeting minutes document</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Signed Board Meeting Minutes Document *
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setBoardMinutesFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, PNG</p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={boardMinutesUploading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {boardMinutesUploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowBoardMinutesForm(false);
                        setBoardMinutesFile(null);
                        setBoardMinutesDate('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* SBA Registration Upload Form */}
            {showSBAForm && (
              <div className="border-t pt-4 mt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit SBA Registration</h3>
                <form onSubmit={handleSBAUpload} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SBC Registration Document *
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setSbaFile(e.target.files[0])}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, JPG, PNG</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      SBC Control ID (9-digit number) *
                    </label>
                    <input
                      type="text"
                      value={sbaControlId}
                      onChange={(e) => setSbaControlId(e.target.value)}
                      placeholder="XXXXXXXXX"
                      maxLength="9"
                      pattern="\d{9}"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the 9-digit control ID from your SBA registration</p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={sbaUploading}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sbaUploading ? 'Submitting...' : 'Submit Registration'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowSBAForm(false);
                        setSbaFile(null);
                        setSbaControlId('');
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Completed Tasks Section */}
        <div className="card p-6">
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => setCompletedTasksExpanded(!completedTasksExpanded)}
          >
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">Completed Tasks</h2>
              <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">
                {(() => {
                  const completedAdminTasks = adminTasks.filter(t => t.status === 'completed').length;
                  const completedFounderTasks = founderTasks.filter(t => t.status === 'completed').length;
                  const completedMonthlyTasks = displayMonths.filter(month => monthlyTransactionStatus[month.value]?.allConfirmed).length;
                  return completedAdminTasks + completedFounderTasks + completedMonthlyTasks;
                })()}
              </span>
            </div>
            <span className="text-gray-500">
              {completedTasksExpanded ? '▼' : '▶'}
            </span>
          </div>
          <p className="text-gray-600 text-sm mt-1">Tasks that have been completed</p>

          {completedTasksExpanded && (
            <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
              {(() => {
                const completedAdminTasks = adminTasks.filter(t => t.status === 'completed');
                const completedFounderTasks = founderTasks.filter(t => t.status === 'completed');
                const completedMonthly = displayMonths.filter(month => monthlyTransactionStatus[month.value]?.allConfirmed);
                const allCompleted = [...completedAdminTasks, ...completedFounderTasks];

                if (allCompleted.length === 0 && completedMonthly.length === 0) {
                  return (
                    <p className="text-gray-500 text-center py-4">No completed tasks yet</p>
                  );
                }

                return (
                  <>
                    {completedAdminTasks.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Admin Tasks</h3>
                        <div className="space-y-2">
                          {completedAdminTasks.map((task, index) => (
                            <div
                              key={`admin-${index}`}
                              className="flex items-center justify-between p-3 rounded-lg border border-green-200 bg-green-50"
                            >
                              <div className="flex items-center gap-3">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </span>
                                <div>
                                  <p className="font-medium text-gray-900">{task.taskName}</p>
                                  {task.completedAt && (
                                    <p className="text-xs text-green-600">
                                      Completed {new Date(task.completedAt).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {completedFounderTasks.length > 0 && (
                      <div className="mb-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Your Tasks</h3>
                        <div className="space-y-2">
                          {completedFounderTasks.map((task, index) => (
                            <div
                              key={`founder-${index}`}
                              className="flex items-center justify-between p-3 rounded-lg border border-green-200 bg-green-50"
                            >
                              <div className="flex items-center gap-3">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </span>
                                <div>
                                  <p className="font-medium text-gray-900">{task.title}</p>
                                  {task.completedAt && (
                                    <p className="text-xs text-green-600">
                                      Completed {new Date(task.completedAt).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {completedMonthly.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Monthly Tasks</h3>
                        <div className="space-y-2">
                          {completedMonthly.map((month) => {
                            const status = monthlyTransactionStatus[month.value];
                            return (
                              <Link
                                key={`completed-monthly-${month.value}`}
                                to={`/grant-fund-financials?tab=confirmed&month=${month.value}`}
                                className="flex items-center justify-between p-3 rounded-lg border border-green-200 bg-green-50 transition-colors duration-200 hover:bg-green-100"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <div>
                                    <p className="font-medium text-gray-900">Transactions: {status?.label || month.label}</p>
                                    <p className="text-xs text-green-600">All {status?.totalCount || 0} transaction{(status?.totalCount || 0) !== 1 ? 's' : ''} confirmed</p>
                                  </div>
                                </div>
                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            {adminTasks
              .filter(task => task.completedAt)
              .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
              .slice(0, 5)
              .map((task, index) => (
                <div key={index} className="flex items-center space-x-3 py-2 border-b border-gray-100 last:border-b-0">
                  <span className="text-green-600 text-lg">✓</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{task.taskName}</p>
                    <p className="text-xs text-gray-500">
                      Completed {new Date(task.completedAt).toLocaleDateString()}
                      {task.completedBy && ` by ${task.completedBy}`}
                    </p>
                  </div>
                </div>
              ))}
            
            {adminTasks.filter(task => task.completedAt).length === 0 && (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>

        {/* Company Info Summary */}
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Company Name</p>
              <p className="font-medium">{submission['Company Name']}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Formation Status</p>
              <p className="font-medium">
                {submission.companyAlreadyFormed ? 'Already Formed' : 'New Formation'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Submitted</p>
              <p className="font-medium">{new Date(submission['Submitted At']).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Founders</p>
              <p className="font-medium">{submission.founders?.length || 0} founder(s)</p>
            </div>
          </div>
        </div>

        {/* Email Confirmation Modal for Certificate Upload */}
        <EmailConfirmationModal
          isOpen={showCertificateConfirmModal}
          onClose={() => setShowCertificateConfirmModal(false)}
          onConfirm={confirmCertificateUpload}
          loading={certificateUploading}
          emailData={{
            recipients: assignedAdmins.length > 0
              ? assignedAdmins.map(admin => ({ email: admin.email, role: admin.name || 'Admin' }))
              : [{ email: 'admin@streamlaunch.com', role: 'Admin Team' }],
            description: `Upload your Certificate of Formation (${certificateFile?.name}) with formation date ${dateOfFormation}.`,
            additionalInfo: 'An email notification will be sent to the admin team informing them that the certificate has been uploaded and is ready for review.'
          }}
        />

        {/* Email Confirmation Modal for Board Meeting Minutes Upload */}
        <EmailConfirmationModal
          isOpen={showBoardMinutesConfirmModal}
          onClose={() => setShowBoardMinutesConfirmModal(false)}
          onConfirm={confirmBoardMinutesUpload}
          loading={boardMinutesUploading}
          emailData={{
            recipients: assignedAdmins.length > 0
              ? assignedAdmins.map(admin => ({ email: admin.email, role: admin.name || 'Admin' }))
              : [{ email: 'admin@streamlaunch.com', role: 'Admin Team' }],
            description: `Upload your Signed Board Meeting Minutes (${boardMinutesFile?.name}).`,
            additionalInfo: 'An email notification will be sent to the admin team informing them that the signed board meeting minutes have been uploaded and are ready for review.'
          }}
        />

        {/* Email Confirmation Modal for SBA Registration Upload */}
        <EmailConfirmationModal
          isOpen={showSBAConfirmModal}
          onClose={() => setShowSBAConfirmModal(false)}
          onConfirm={confirmSBAUpload}
          loading={sbaUploading}
          emailData={{
            recipients: assignedAdmins.length > 0
              ? assignedAdmins.map(admin => ({ email: admin.email, role: admin.name || 'Admin' }))
              : [{ email: 'admin@streamlaunch.com', role: 'Admin Team' }],
            description: `Submit your SBA Registration (${sbaFile?.name}) with Control ID: ${sbaControlId}.`,
            additionalInfo: 'An email notification will be sent to the admin team informing them that your SBA registration has been submitted and is ready for review.'
          }}
        />
      </div>
    </div>
  );
};

export default FounderTodolist;